// The optimistic brake's state machine (Brief 4 / P2-B). ONE reducer, fed by
// BOTH the Orchestrator's optimistic action (the instant echo of MY action) and
// the truthful presence SSE (THEIR state) — so a confirmed state is NEVER
// rendered before the server says so (UC-4: "optimism for my actions, verified
// truth for theirs"). A divergent confirm RECONCILES to the truth rather than
// sticking on optimism or flashing a false "all clear". Pure + host-tested; the
// React component dispatches `send` on tap, `presence` on each SSE frame,
// `timeout` from a safety timer, `fail` when the optimistic POST errors.

import { statusLabel } from "@waytide/ui";

export interface BrakeState {
  /** The latest CONFIRMED presence status (the truth from the SSE / initial read). */
  status: string;
  /** An action the Orchestrator sent that is awaiting server confirmation; null
   * once settled. While set, the control reads as PENDING — never as confirmed. */
  pending: null | "pause" | "resume";
}

export type BrakeAction =
  | { type: "send"; intent: "pause" | "resume" }
  | { type: "presence"; status: string }
  | { type: "timeout" }
  | { type: "fail" };

/** A presence status that means a pending pause/resume did NOT land as asked —
 * reconcile to it (the session moved elsewhere: it finished, it needs the human,
 * or an Operator halted it) rather than keep waiting or fake a confirm. */
function isDivergent(status: string): boolean {
  return status === "done" || status === "needs_input" || status === "halted";
}

export function brakeReducer(
  state: BrakeState,
  action: BrakeAction,
): BrakeState {
  switch (action.type) {
    case "send":
      // Optimistic echo — MY action, as sent. Never renders THEIR state resolved.
      return { ...state, pending: action.intent };
    case "fail":
      // The optimistic POST errored — drop the echo; never stick on optimism.
      return { ...state, pending: null };
    case "presence": {
      const status = action.status;
      if (state.pending === "pause") {
        if (status === "paused") return { status, pending: null }; // CONFIRMED
        if (isDivergent(status)) return { status, pending: null }; // reconcile to truth
        // running/idle: the halt is in flight — hold pending, never a false confirm.
        return { ...state, status };
      }
      if (state.pending === "resume") {
        if (status === "running" || status === "idle")
          return { status, pending: null }; // CONFIRMED
        if (isDivergent(status)) return { status, pending: null }; // reconcile
        // paused: the resume is in flight — hold pending.
        return { ...state, status };
      }
      return { ...state, status }; // settled: track the confirmed truth
    }
    case "timeout":
      // Gave up waiting for a confirm — reconcile to the latest known truth
      // (drop the pending echo); never leave the control stuck on optimism.
      return state.pending ? { ...state, pending: null } : state;
  }
}

/** What the control reads RIGHT NOW. `pending` is visually distinct from a
 * confirmed state — the caller renders it dimmed + "…" and never as fact, so a
 * reconcile-to-different-truth reads as "the world updated", not "the UI lied". */
export function brakeDisplay(state: BrakeState): {
  label: string;
  pending: boolean;
} {
  if (state.pending === "pause") return { label: "Stopping…", pending: true };
  if (state.pending === "resume") return { label: "Resuming…", pending: true };
  return { label: statusLabel(state.status), pending: false };
}
